import React, { useEffect, useState } from "react";
import { Autocomplete, Box, Button, Chip, Grid2, Link, Stack, Typography, useTheme, } from "@mui/material";
import TableComponent from "components/grid";
import { useNavigate, useParams } from "react-router-dom";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useGetPendingDocumentsQuery } from "store/slices/apis/documentsSlice";
import moment from "moment";
import PaginationComponent from "components/ui-components/PaginationComponent";
import { API_END_POINT } from "config";
import ApproveDocument from "./ApproveDocument";
import { closeSnackbar, openSnackbar } from "store/slices/snackbar";
import { useDispatch } from "store";
import RejectDocument from "./RejectDocument";
import { MdArrowBackIosNew } from "react-icons/md";
import { ReactComponent as CalendarIcon } from "assets/PwC_Funct_Icons_Calendar_Outline_Black_RGB.svg"

const columns = [
    {
        name: "Document ID",
        field: "issueNumber",
        renderer(params) {
            return (
                <Stack direction="row" spacing={1}>
                    {params?.documentId &&
                        <Chip label={params?.documentId} size="small" sx={{
                            fontSize: '12px',
                            '& .MuiChip-label': {
                                paddingLeft: '8px',
                                paddingRight: '8px',
                                fontWeight: 600
                            }
                        }} />
                    }
                </Stack>
            );
        }
    },
    {
        name: "Document Name",
        field: "documentName",
    },
    {
        name: "Plan Date",
        field: "planDate",
        renderer(params) {
            return (
                <>
                    {params?.planDate
                        ? moment(params.planDate).format("DD/MM/YY")
                        : ""}
                </>
            );
        },
    },
    {
        name: "Activity Name",
        field: "activityName",
    },
    {
        name: "Activity Start Date",
        field: "activitySatartDate",
        renderer(params) {
            return (
                <>
                    {params?.activitySatartDate
                        ? moment(params.activitySatartDate).format("DD/MM/YY")
                        : ""}
                </>
            );
        },
    },
    {
        name: "Activity End Date",
        field: "activityEndDate",
        renderer(params) {
            return (
                <>
                    {params?.activityEndDate
                        ? moment(params.activityEndDate).format("DD/MM/YY")
                        : ""}
                </>
            );
        },
    },
    {
        name: "Created Date",
        field: "createdDate",
        renderer(params) {
            return (
                <>
                    {params?.createdDate
                        ? moment(params.createdDate).format("DD/MM/YY")
                        : ""}
                </>
            );
        },
    },
    {
        name: 'Action',
        field: 'action',
        option: {
            view: true,
            download: true,
            acceptReject: true
        }
    },
];

const PendingApprovalDocuments = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const theme = useTheme();
    const { projectId } = useParams();
    const { activityId } = useParams();

    const [isDocumentApprove, setIsDocumentApprove] = useState(false);
    const [isDocumentReject, setIsDocumentReject] = useState(false);

    const [rowData, setRowData] = useState(null);

    const [pageSize, setPageSize] = useState(10);
    const [pageNumber, setPageNumber] = useState(1);

    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);

    const [filters, setFilters] = useState({
        fromDate: "",
        toDate: ""
    });

    const [pendingListData, setPendingListData] = useState([]);

    const { data: pendingList, isLoading, isSuccess: pendingIsSuccess, isError: pendingIsError } = useGetPendingDocumentsQuery({
        projectId: projectId,
        activityId: activityId,
        fromDate: filters?.fromDate,
        toDate: filters?.toDate,
        page: pageNumber,
        pageSize: pageSize,
    });

    useEffect(() => {
        if (pendingIsSuccess) {
            setPendingListData(pendingList?.data?.data)
        }
    }, [pendingList, pendingIsSuccess, pendingIsError])

    const handleActionView = (row) => {
        window.open(`${API_END_POINT}issues/view/${row?.docFileName}`, '_blank');
    }

    const handleActionDownload = async (row) => {

        // try {
        //     const response = await fetch(`${API_END_POINT}issues/view/${row?.docFileName}`, {
        //         method: 'GET',
        //         headers: {
        //             // If you need auth headers, add here
        //             // 'Authorization': 'Bearer YOUR_TOKEN',
        //         },
        //     });

        //     if (!response.ok) throw new Error('Failed to download file');

        //     const blob = await response.blob();
        //     const blobUrl = window.URL.createObjectURL(blob);

        //     const link = document.createElement('a');
        //     link.href = blobUrl;
        //     link.download = row?.documentName;
        //     document.body.appendChild(link);
        //     link.click();

        //     // Cleanup
        //     link.remove();
        //     window.URL.revokeObjectURL(blobUrl);
        // } catch (err) {
        //     console.error('Error downloading file:', err);
        //     openNotification('Download failed. Please check the file URL or network.');
        // }
    }

    const handleActionAcceptDocument = (row) => {
        setIsDocumentApprove((prev) => {
            const newState = !prev;
            setRowData(newState ? row : null);
            return newState;
        });
    }

    const handleActionRejectDocument = (row) => {
        setIsDocumentReject((prev) => {
            const newState = !prev;
            setRowData(newState ? row : null);
            return newState;
        });
    }

    const CustomCalendarIcon = (props) => (
        <CalendarIcon {...props} style={{ width: 26, height: 26, fill: "#696969 ", stroke: "#696969", strokeWidth: 4 }} />
    );

    const openNotification = async (msg, type = 'error') => {

        await dispatch(
            openSnackbar({
                open: true,
                message: msg,
                variant: 'alert',
                alert: {
                    color: type
                },
                close: false
            })
        );
        setTimeout(() => dispatch(closeSnackbar()), 3000);
    };

    return (
        <>
            <Grid2 container spacing={2} sx={{ mt: "20px" }}>
                <Grid2 size={12}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <Stack
                            direction="row"
                            spacing={2}
                            sx={{
                                flexGrow: 2,
                                alignItems: "center",
                                "& .MuiStack-root": {
                                    paddingTop: "15px",
                                },
                            }}
                        >
                            <LocalizationProvider dateAdapter={AdapterDayjs} size="small">
                                <DemoContainer components={["DatePicker", "DatePicker"]}>
                                    <DatePicker
                                        label="From Date"
                                        value={fromDate}
                                        format="DD-MM-YYYY"
                                        onChange={(newValue) => setFromDate(newValue)}
                                        slots={{
                                            openPickerIcon: CustomCalendarIcon,
                                        }}
                                        slotProps={{
                                            textField: {
                                                sx: {
                                                    width: 100,
                                                    "& .MuiOutlinedInput-input": {
                                                        padding: "10px",
                                                    },

                                                    "& .MuiInputLabel-root": {
                                                        top: "-6px",
                                                    },
                                                },
                                            },
                                            field: {
                                                clearable: true,
                                                onClear: () =>
                                                    setFilters({
                                                        fromDate: "",
                                                        toDate: toDate
                                                            ? dayjs(toDate).format("YYYY-MM-DD")
                                                            : "",
                                                    }),
                                            },
                                        }}
                                    />
                                </DemoContainer>
                            </LocalizationProvider>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DemoContainer components={["DatePicker", "DatePicker"]}>
                                    <DatePicker
                                        label="To Date"
                                        value={toDate}
                                        format="DD-MM-YYYY"
                                        clearable={true}
                                        onChange={(newValue) => setToDate(newValue)}
                                        slots={{
                                            openPickerIcon: CustomCalendarIcon,
                                        }}
                                        slotProps={{
                                            textField: {
                                                sx: {
                                                    width: 100,
                                                    "& .MuiOutlinedInput-input": {
                                                        padding: "10px",
                                                    },

                                                    "& .MuiInputLabel-root": {
                                                        top: "-6px",
                                                    },
                                                },
                                            },
                                            field: {
                                                clearable: true,
                                                onClear: () =>
                                                    setFilters({
                                                        fromDate: fromDate
                                                            ? dayjs(fromDate).format("YYYY-MM-DD")
                                                            : "",
                                                        toDate: "",
                                                    }),
                                            },
                                        }}
                                    />
                                </DemoContainer>
                            </LocalizationProvider>
                            <Button
                                // loading={isLoading}
                                onClick={() => {
                                    setFilters({
                                        fromDate: fromDate
                                            ? dayjs(fromDate).format("YYYY-MM-DD")
                                            : "",
                                        toDate: toDate ? dayjs(toDate).format("YYYY-MM-DD") : ""
                                    });
                                }}
                                size="large"
                                variant="outlined"
                                sx={{
                                    color: `${theme?.customSettings?.themeColor}`,
                                    border: `1px solid ${theme?.customSettings?.themeColor}`,
                                    background: "none",
                                    textTransform: "inherit",
                                    padding: "6px 15px",
                                    marginTop: "12px !important",
                                }}
                            >
                                Search
                            </Button>
                            <Button
                                // loading={isLoading}
                                onClick={() => {
                                    setFromDate(null)
                                    setToDate(null)
                                    const clearedValues = Object.fromEntries(
                                        Object.keys(filters).map(key => [key, ""])
                                    );
                                    setFilters(clearedValues);
                                }}
                                size="large"
                                variant="outlined"
                                sx={{
                                    color: `${theme?.customSettings?.themeColor}`,
                                    border: `1px solid ${theme?.customSettings?.themeColor}`,
                                    background: "none",
                                    textTransform: "inherit",
                                    padding: "6px 15px",
                                    marginTop: "12px !important",
                                }}
                            >
                                Reset
                            </Button>
                        </Stack>
                        <Button
                            variant="contained"
                            startIcon={<MdArrowBackIosNew sx={{ marginBottom: "2px" }} />}
                            onClick={() => {
                                navigate(`/projects/${projectId}/engineering/all-activities`)
                            }}
                            // loading={updateManpowerRes?.isLoading}
                            sx={{
                                backgroundColor: `${theme?.customSettings?.themeColor}`,
                                color: "#FFF",
                                padding: "5px 20px",
                                fontWeight: "500",
                                textTransform: "none",
                                fontSize: "16px",
                                marginTop: "11px",
                                marginLeft: 1,
                                border: `1px solid ${theme?.customSettings?.themeColor}`,
                                '&.Mui-disabled': {
                                    border: 'none'
                                }
                            }}
                        >
                            Activity List
                        </Button>
                    </Box>
                </Grid2>
                <Grid2 size={12} sx={{ mt: 2 }}>
                    <TableComponent
                        columns={columns}
                        data={pendingListData}
                        type="border"
                        actionView={handleActionView}
                        actionDownload={handleActionDownload}
                        actionAccept={handleActionAcceptDocument}
                        actionReject={handleActionRejectDocument}
                    />
                    <Box sx={{ display: "flex", justifyContent: "end" }}>
                        <PaginationComponent
                            pageNumber={pendingList?.data?.pagination?.currentPage || 1}
                            totalPages={pendingList?.data?.pagination?.totalPages || 1}
                            onPageChange={setPageNumber}
                        />
                    </Box>
                </Grid2>
            </Grid2>
            {isDocumentApprove &&
                <ApproveDocument open={isDocumentApprove} cancel={handleActionAcceptDocument} rowData={rowData} openNotification={openNotification} />
            }
            {isDocumentReject &&
                <RejectDocument open={isDocumentReject} cancel={handleActionRejectDocument} rowData={rowData} openNotification={openNotification} />
            }
        </>
    );
}

export default PendingApprovalDocuments;
